import { Route } from 'vue-router';

import Store from '@/main/store';

import { validateToken } from '@/api/resources/customer';

import { INavigationGuardRoute, INavigationGuardRules } from '@/interfaces/NavigationGuard';

import { isValid } from '@/helpers/values';
import {
  ENavigationGuardTypes,
  ECheckoutStep,
  EAuthenticationAction,
  EAuthenticationType,
  EAuthenticationOrigin,
  EAuthenticationTokenStatus
} from '@/helpers/enums';
import { tokenDecoder } from './token';
import { goToDashboard } from './redirects';
import {
  pushAuthenticationActionToDatalayer,
  getAuthEventOnSession,
  pushCurrentActaUserIdToDatalayer
} from './dataLayer';
import { EInteractEventTrackerAction } from '@/app/features/InteractEventTracker/enums/EInteractEventTracker';
import { sendInteractEvent } from './events';

const redirectIfExistOnUrl = (route: Route): void => {
  const nextUrl = route.query.next;

  isValid(nextUrl) && window.location.replace(nextUrl as string);
};

const setTokenIfExistOnUrl = (route: Route): any => {
  const token = route.query.t;

  isValid(token) && Store.commit('App/Customer/updateToken', token);

  return token;
};

const redirectIfIsGoingToDashboard = (route: Route): void => {
  if (route.meta.navigationGuardType === 'authenticationDashboard') {
    goToDashboard();
  }
};

const authRouteRedirectByRoute = (navigationGuardRules: INavigationGuardRules, route: Route): any => {
  if (['renew', 'removeRecurrence', 'enableRecurrence'].includes(route.name!)) {
    const allowedIncomingQueryParams = Object.fromEntries(Object.entries(route.query)
      .filter((entrie: [string, string | (string | null)[]]) => entrie[0] !== 'goBackRoute')
    );
    const incomingQueryParamsString = new URLSearchParams(allowedIncomingQueryParams as Record<string, string>).toString();

    return {
      name: 'loginToDashboard',
      params: route.params,
      query: {
        ...route.query,
        goBackRoute: `${route.path}?${incomingQueryParamsString}`,
      },
    };
  }

  return {
    name: navigationGuardRules.authentication,
    params: route.params,
    query: route.query,
  };
};

const navigationGuard = (navigationGuardRules: INavigationGuardRules, routes: INavigationGuardRoute[]): INavigationGuardRoute[] => {
  return routes.map((route: INavigationGuardRoute) => {
    const beforeEnter = async (to: Route, from: Route, next: any): Promise<any> => {
      const hasUrlToken = setTokenIfExistOnUrl(to);
      redirectIfExistOnUrl(to);

      const defaultToken = Store.state.App.Customer.token.getSession().value;

      const token: string = (!hasUrlToken)
        ? defaultToken
          ? defaultToken
          : ''
        : hasUrlToken;

      isValid(token) && Store.commit('App/Customer/updateToken', token);

      const toNavigationGuardType: string = to.meta.navigationGuardType
        ? to.meta.navigationGuardType
        : ENavigationGuardTypes.public;

      let checkoutStep: ECheckoutStep = to.meta.checkoutStep;

      try {
        await validateToken(token);

        const isUserLogged = Store.getters['App/Customer/isLogged'];
        const customer = Store.state.App.Customer.customer;

        (!isUserLogged) && Store.commit('App/Customer/successSignin', token);
        (!isUserLogged) && pushCurrentActaUserIdToDatalayer();

        if (route.meta.navigationGuardType === 'authenticationDashboard') {
          pushAuthenticationActionToDatalayer({
            type: EAuthenticationType.automatic,
            action: EAuthenticationAction.loginAs,
            origin: EAuthenticationOrigin.guard,
            status: EAuthenticationTokenStatus.valid,
          });
        } else {
          pushAuthenticationActionToDatalayer({
            type: EAuthenticationType.automatic,
            action: EAuthenticationAction.login,
            origin: EAuthenticationOrigin.guard,
            status: EAuthenticationTokenStatus.valid,
          });
        }

        if (isUserLogged && !customer.document) {
          Store.dispatch('App/Customer/details', { root: true });
        }

        if (!isUserLogged) {
          sendInteractEvent({
            action: EInteractEventTrackerAction.CHK_LOGGED_ACCESS_SUCCESS,
            attributes: {
              return_code: 200,
              return_message: 'OK',
            },
            options: {
              avoidDuplicate: true,
            },
          });
        }

        redirectIfIsGoingToDashboard(to);

        (toNavigationGuardType === ENavigationGuardTypes.private)
          ? next()
          : (toNavigationGuardType === ENavigationGuardTypes.authentication)
            ? next({
              name: navigationGuardRules.private,
              params: to.params,
              query: to.query,
            }) : next();
      } catch (error) {
        checkoutStep = ECheckoutStep.authentication;

        (toNavigationGuardType === ENavigationGuardTypes.private)
          ? next(authRouteRedirectByRoute(navigationGuardRules, to))
          : next();

        if (!isValid(token)) {
          if (toNavigationGuardType !== ENavigationGuardTypes.private) {
            sendInteractEvent({
              action: EInteractEventTrackerAction.CHK_UNLOGGED_ACCESS_SUCCESS,
              attributes: {
                return_code: 200,
                return_message: 'OK',
              },
              options: {
                avoidDuplicate: true,
              },
            });
          } else {
            sendInteractEvent({
              action: EInteractEventTrackerAction.CHK_UNLOGGED_ACCESS_FAILED,
              attributes: {
                return_code: '',
                return_message: 'redirect',
              },
              options: {
                avoidDuplicate: true,
              },
            });
          }
        }

        if (isValid(token) && error) {
          if (error.response) {
            pushAuthenticationActionToDatalayer({
              type: EAuthenticationType.automatic,
              action: EAuthenticationAction.logout,
              origin: EAuthenticationOrigin.guard,
              status: EAuthenticationTokenStatus.invalid,
            });

            Store.commit('App/Customer/removeToken');

            sendInteractEvent({
              action: EInteractEventTrackerAction.CHK_LOGGED_ACCESS_FAILED,
              attributes: {
                return_code: error.response.status,
                return_message: EAuthenticationTokenStatus.invalid,
              },
              options: {
                avoidDuplicate: true,
              },
            });
          } else if (!tokenDecoder(token)) {
            pushAuthenticationActionToDatalayer({
              type: EAuthenticationType.automatic,
              action: EAuthenticationAction.logout,
              origin: EAuthenticationOrigin.guard,
              status: EAuthenticationTokenStatus.unreadable,
            });

            sendInteractEvent({
              action: EInteractEventTrackerAction.CHK_LOGGED_ACCESS_FAILED,
              attributes: {
                return_code: error.response.status,
                return_message: EAuthenticationTokenStatus.unreadable,
              },
              options: {
                avoidDuplicate: true,
              },
            });
          }
        } else if (getAuthEventOnSession(EAuthenticationAction.login)) {
          pushAuthenticationActionToDatalayer({
            type: EAuthenticationType.automatic,
            action: EAuthenticationAction.logout,
            origin: EAuthenticationOrigin.guard,
            status: EAuthenticationTokenStatus.removed,
          });
        }
      } finally {
        Store.commit('App/setStep', checkoutStep);
      }
    };

    return {
      ...route,
      ...route.children && { children: navigationGuard(navigationGuardRules, route.children) },
      beforeEnter,
    };
  });
};

export default navigationGuard;
